/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import withMediaQuery from "dc-preview-utils/src/components/withMediaQuery";
import HighContrastCss from "./HighContrastSelection.css";
import { QUAD_TYPE, mqTypes } from "../../Constants/enums";
import {
  FC_ACTIVE,
  pageContentTypes,
  IMAGE_BORDER
} from "../../Constants/constants";
import { mergeQuads } from "../../Utils/SelectionUtils";

class PageHighlights extends Component {
  componentDidUpdate() {}

  _getSvgBounds(quad) {
    const { left, top, right, bottom, width, height } = quad.polygonBoundsUnion;
    return {
      left,
      top,
      right,
      bottom,
      width,
      height
    };
  }

  _getBackGroundColor(quad) {
    const { color } = quad;
    const backgroundColor = `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.alpha})`;
    return backgroundColor;
  }

  // TODO: use functional component & React.memo() for better performance
  _drawQuads() {
    const originalQuads = this.props.quads;
    const isImage = this.props.contentType === pageContentTypes.IMAGE;
    const selectionBoxes = [];
    const drawBorder =
      this.props.isHCFlagEnabled && this.props.mqState === FC_ACTIVE;
    if (originalQuads && originalQuads.length > 0) {
      let quads = originalQuads;
      if (drawBorder) {
        quads = mergeQuads(originalQuads);
      }
      quads.forEach(quad => {
        const svgBounds = this._getSvgBounds(quad);
        const svgStyle = {
          position: "absolute",
          ...svgBounds,
          pointerEvents: "none",
          border: isImage
            ? `${IMAGE_BORDER.COLOR} ${IMAGE_BORDER.STYLE} ${IMAGE_BORDER.SIZE} `
            : "none"
        };
        const backgroundColor = this._getBackGroundColor(quad);
        const { left, top, right, bottom } = svgBounds;
        const key = `${left}${right}${top}${bottom}${backgroundColor}`;
        const polygonStyle = {
          fill: drawBorder ? "none" : backgroundColor,
          pointerEvents: "fill"
        };
        const hcSelClass = classNames(
          drawBorder &&
            (quad.type === QUAD_TYPE.DEF_SEARCH_RESULT
              ? HighContrastCss.HCSelectionDefault
              : HighContrastCss.HCSelection)
        );
        const { polygonPoints: translatedPoints } = quad.translate(
          svgBounds.left,
          svgBounds.top
        );

        selectionBoxes.push(
          <svg style={svgStyle} key={key}>
            <polygon
              points={translatedPoints}
              style={polygonStyle}
              className={hcSelClass}
            />
          </svg>
        );
      });

      if (this.props.getIsTouchSelectionInProgress === true) {
        const pageIndex = this.props.pageIndex;
        this.props.setTouchSelPageIndex(pageIndex);
        this.props.setShowGrabbers(true);
      }
    }
    return selectionBoxes;
  }
  render() {
    const highlights = this._drawQuads();
    return <div>{highlights}</div>;
  }
}

PageHighlights.propTypes = {
  getIsTouchSelectionInProgress: PropTypes.bool.isRequired,
  contentType: PropTypes.number,
  quads: PropTypes.arrayOf(
    PropTypes.shape({
      quadPoints: PropTypes.arrayOf(PropTypes.number),
      originalQuadPointsArray: PropTypes.arrayOf(
        PropTypes.arrayOf(PropTypes.number)
      ),
      type: PropTypes.number,
      color: PropTypes.shape({
        red: PropTypes.number,
        green: PropTypes.number,
        blue: PropTypes.number,
        alpha: PropTypes.number
      })
    })
  ).isRequired,
  pageIndex: PropTypes.number.isRequired,
  setTouchSelPageIndex: PropTypes.func.isRequired,
  setShowGrabbers: PropTypes.func.isRequired,
  isHCFlagEnabled: PropTypes.bool,
  mqState: PropTypes.string.isRequired
};

PageHighlights.defaultProps = {
  isHCFlagEnabled: false,
  contentType: pageContentTypes.NONE
};

const wrap = (wrappedComponent, props) => {
  let Comp = wrappedComponent;
  if (props.isHCFlagEnabled) {
    Comp = withMediaQuery(wrappedComponent, mqTypes.FORCED_COLORS);
  }
  return <Comp {...props} />;
};

const Wrapper = wrappedComponent => props => wrap(wrappedComponent, props);

wrap.propTypes = {
  isHCFlagEnabled: PropTypes.bool
};
wrap.defaultProps = {
  isHCFlagEnabled: false,
  contentType: pageContentTypes.NONE
};

export default Wrapper(PageHighlights);
